import React, { ReactNode, ErrorInfo } from 'react';

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
}

/**
 * RouteErrorBoundary
 *
 * Wraps the route tree so a render-time throw in any page/component
 * (e.g. reactstrap TooltipPopoverWrapper.render accessing this._targets[0]
 * when _targets is null after unmount) shows a VISIBLE fallback instead of
 * unmounting the whole React root (blank page).
 *
 * componentDidCatch logs the componentStack to the console — there is no
 * frontend Sentry, so this is the only way to see which component crashed
 * (needed for the permanent fix).
 *
 * Pattern referenced from AnimationErrorBoundary.jsx, but this boundary
 * renders a visible fallback (NOT null). The fallback uses only plain HTML +
 * inline styles — no reactstrap / tooltip / framer-motion — so the fallback
 * itself cannot re-trigger the same crash. The single action is a full-page
 * reload, which guarantees a clean state (no stale boundary lock-in).
 */
class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): RouteErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // componentStack pinpoints the crashing component (e.g. the offending
    // Tooltip/Popover instance) — critical for the permanent fix.
    console.error(
      '[RouteErrorBoundary] Çöken bileşen:',
      error?.message,
      errorInfo?.componentStack
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            backgroundColor: '#f3f3f9',
          }}
        >
          <div
            style={{
              maxWidth: 420,
              width: '100%',
              textAlign: 'center',
              backgroundColor: '#ffffff',
              borderRadius: 8,
              boxShadow: '0 1px 2px rgba(56, 65, 74, 0.15)',
              padding: '32px 24px',
            }}
          >
            <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 16 }}>⚠️</div>
            <h5 style={{ margin: '0 0 8px', color: '#495057', fontWeight: 600 }}>
              Sayfada bir sorun oluştu
            </h5>
            <p style={{ margin: '0 0 24px', color: '#878a99', fontSize: 14 }}>
              Beklenmeyen bir hata nedeniyle bu sayfa görüntülenemedi. Lütfen
              sayfayı yeniden yükleyin.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#405189',
                color: '#ffffff',
                border: 'none',
                borderRadius: 4,
                padding: '8px 20px',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Yeniden Yükle
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
